import React, { useEffect, useState } from 'react';
import { MessageCircle, X, Bug, Lightbulb, Palette, HelpCircle, Send, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { id: 'bug', label: 'Bug', icon: Bug, tint: 'bg-red-500' },
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb, tint: 'bg-amber-500' },
  { id: 'ux', label: 'UX', icon: Palette, tint: 'bg-violet-500' },
  { id: 'other', label: 'Other', icon: HelpCircle, tint: 'bg-zinc-500' },
];

// Floating "Feedback" pill visible on every authenticated page. Beta
// testers (or any signed-in user) can log bug reports, UX notes and
// suggestions without leaving the page they're on — the current
// page URL is captured automatically so SuperAdmins can navigate
// straight to the reported spot.
function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pages that already have a fixed-bottom message composer OR a fixed-bottom
  // navigation bar: the floating Feedback pill is hidden on these so it does
  // not cover the Send button or the typing box.  Tudor review 9 Oct 2026 (B2).
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const hideOnThisPage = (
    path.startsWith('/inbox') ||
    path.startsWith('/group-chat') ||
    path.startsWith('/dm') ||
    path.startsWith('/direct-messages') ||
    path.startsWith('/relapse-message') ||
    path.startsWith('/bot')
  );

  // Native-app preference (single Yes/No/Maybe question). Loaded on modal
  // open; saved immediately on click so the user doesn't have to hit
  // Submit for the choice to persist. Independent of the free-text
  // feedback flow.
  const [nativePref, setNativePref] = useState(null);
  const [nativePrefSaving, setNativePrefSaving] = useState(null); // 'yes' | 'no' | 'maybe' | null

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/beta-feedback/native-app-preference`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setNativePref(data.preference || null);
      } catch {
        /* silent: this is a nice-to-have */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  const saveNativePref = async (choice) => {
    if (nativePrefSaving) return;
    setNativePrefSaving(choice);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/beta-feedback/native-app-preference`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preference: choice }),
        }
      );
      if (!res.ok) throw new Error('Save failed');
      setNativePref(choice);
      toast.success('Thanks: noted!');
    } catch {
      toast.error('Could not save your answer');
    } finally {
      setNativePrefSaving(null);
    }
  };

  // Hide the widget entirely when there is no signed-in user.
  if (!user) return null;

  const reset = () => {
    setCategory('bug');
    setDescription('');
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (description.trim().length < 3) {
      toast.error('Please add a few more words so we can act on it');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          description: description.trim(),
          page_url: typeof window !== 'undefined' ? window.location.href : null,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      toast.success('Thank you: feedback sent!');
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err.message || 'Could not send feedback');
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button: bottom-right on every page */}
      {!open && !hideOnThisPage && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-3 z-40 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white shadow hover:bg-zinc-800 transition-all hover:scale-105 border border-black"
          data-testid="feedback-widget-btn"
          aria-label="Send feedback"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span className="text-xs font-semibold">Feedback</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={handleClose}
          data-testid="feedback-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-xl border border-zinc-200"
            role="dialog"
            aria-labelledby="feedback-title"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 id="feedback-title" className="text-lg font-bold text-zinc-950">
                  Send us feedback
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">Help us make Axiom better. All input is read.</p>
              </div>
              <button
                onClick={handleClose}
                className="text-zinc-400 hover:text-zinc-900 p-1 rounded"
                data-testid="feedback-close-btn"
                aria-label="Close"
                disabled={submitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category chips */}
              <div>
                <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">
                  Type
                </label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {CATEGORIES.map(({ id, label, icon: Icon, tint }) => {
                    const active = category === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setCategory(id)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                          active
                            ? `${tint} text-white border-transparent shadow-md`
                            : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                        }`}
                        data-testid={`feedback-category-${id}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-[11px] font-semibold">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="feedback-description" className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">
                  What happened / what would you like?
                </label>
                <textarea
                  id="feedback-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    category === 'bug'
                      ? 'e.g. "Journal entry lost when I clicked back": include steps if you can'
                      : category === 'suggestion'
                      ? 'e.g. "Add a weekly summary email"'
                      : category === 'ux'
                      ? 'e.g. "The mental health form is hard to read on mobile"'
                      : 'Anything else you want to share…'
                  }
                  rows={5}
                  maxLength={4000}
                  className="mt-2 w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
                  data-testid="feedback-description-input"
                  autoFocus
                />
                <p className="mt-1 text-[10px] text-zinc-400 text-right">
                  {description.length} / 4000
                </p>
              </div>

              {/* Context info */}
              <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2">
                <div><strong>Page:</strong> {typeof window !== 'undefined' ? window.location.pathname : ''}</div>
                <div><strong>From:</strong> {user.email}</div>
              </div>

              {/* Native-app preference (single question, saved independently) */}
              <div
                className="border border-zinc-200 rounded-lg p-3 bg-blue-50/40"
                data-testid="native-app-preference-block"
              >
                <div className="flex items-start gap-2 mb-2">
                  <Smartphone className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-semibold text-zinc-800 leading-snug">
                    Would you prefer to download this as a native app from the App Store or Google Play?
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {['yes', 'no', 'maybe'].map((choice) => {
                    const active = nativePref === choice;
                    const saving = nativePrefSaving === choice;
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={() => saveNativePref(choice)}
                        disabled={!!nativePrefSaving}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-400'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`native-app-pref-${choice}`}
                        aria-pressed={active}
                      >
                        {saving ? '…' : choice.charAt(0).toUpperCase() + choice.slice(1)}
                      </button>
                    );
                  })}
                </div>
                {nativePref && (
                  <p className="mt-2 text-[10px] text-zinc-500">
                    Your answer is saved: you can change it any time.
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || description.trim().length < 3}
                className="w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors"
                data-testid="feedback-submit-btn"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackWidget;
